import { Box, Span } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BackIconButton } from './back-icon-button';
import { CloseButton } from './close-button';
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from './dialog';
import ProgressDots from './progress-dots';

/** Header descriptor for the shell's current step. */
export interface ModalShellStep {
  title: string;
  subtitle?: string;
  /** Show the back affordance (left of the title). */
  showBack?: boolean;
  onBack?: () => void;
  /** Progress dots; omit (or `count <= 1`) to hide. */
  dots?: { count: number; current: number };
}

interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  step: ModalShellStep;
  maxWidth?: string;
  /** Pictogram pinned left of the title block, inside the shell's icon pad. */
  headerIcon?: ReactNode;
  /** Status element pinned between the title block and the close button. */
  headerAddon?: ReactNode;
  /**
   * Controls pinned between the header and the scrolling body — filters, tabs,
   * a search field. A band placed inside the body instead has to be made sticky
   * and pulled up by a negative margin, and the seam that leaves lets content
   * scroll through the gap above it.
   */
  subHeader?: ReactNode;
  /** Actions pinned below the body; omit to keep them inside the body. */
  footer?: ReactNode;
  /**
   * Hold the modal open while work is in flight. One flag drives all three
   * dismissal routes — escape, outside press, and the close button, which goes
   * disabled with a reason rather than staying clickable and inert.
   */
  dismissDisabled?: boolean;
  children: ReactNode;
}

const ModalShellHeader = ({
  step,
  icon,
  addon,
  onClose,
  dismissDisabled,
}: {
  step: ModalShellStep;
  icon?: ReactNode;
  addon?: ReactNode;
  onClose: () => void;
  dismissDisabled?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <DialogHeader
      flex="none"
      display="flex"
      alignItems="center"
      gap="3"
      px="22px"
      pt="20px"
      pb="16px"
      borderBottomWidth="1px"
      borderColor="border.subtle"
    >
      {step.showBack && <BackIconButton aria-label={t('common.back')} onClick={step.onBack} />}

      {/* Decorative on purpose: the title beside it already names the modal, so
          announcing the pictogram would only repeat it. */}
      {icon && (
        <Box
          aria-hidden
          flex="none"
          boxSize="34px"
          display="grid"
          placeItems="center"
          borderRadius="9px"
          bg="softCream"
          color="ink"
        >
          {icon}
        </Box>
      )}

      <Box flex="1" minW="0">
        <DialogTitle
          fontFamily="heading"
          fontSize="17px"
          fontWeight="semibold"
          letterSpacing="-0.015em"
          color="dark"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {step.title}
        </DialogTitle>
        {step.subtitle && (
          <Span
            display="block"
            fontSize="xs"
            color="fg.muted"
            mt="0.5"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {step.subtitle}
          </Span>
        )}
      </Box>

      {addon}

      {step.dots && (
        <ProgressDots
          count={step.dots.count}
          current={step.dots.current}
          label={t('common.stepProgress', {
            current: step.dots.current + 1,
            total: step.dots.count,
          })}
        />
      )}

      <CloseButton
        boxSize="30px"
        minW="30px"
        flex="none"
        disabled={dismissDisabled}
        // Native tooltip so the reason survives the disabled state, the same
        // affordance the Studio top bar uses for its unavailable actions.
        title={dismissDisabled ? t('common.closeDisabled') : undefined}
        onClick={onClose}
        aria-label={t('common.close')}
      />
    </DialogHeader>
  );
};

/**
 * Shared modal chrome for the dashboard's dialogs: a header with an optional
 * back button, a per-step title + subtitle, progress dots, and the canonical
 * close button, above a scrollable body. Built on the `dialog.tsx` primitives
 * (positioner pinned above the contact drawer). No shadow — the border and
 * backdrop blur carry the separation.
 *
 * The footer is an optional slot for decision modals, whose confirm/cancel pair
 * must stay reachable while the body scrolls; stepped flows leave it unset and
 * keep their actions in the body, colocated with the step that drives them.
 */
const ModalShell = ({
  isOpen,
  onClose,
  step,
  maxWidth = '640px',
  headerIcon,
  headerAddon,
  subHeader,
  footer,
  dismissDisabled,
  children,
}: ModalShellProps) => {
  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
      placement="center"
      closeOnEscape={!dismissDisabled}
      closeOnInteractOutside={!dismissDisabled}
    >
      <DialogContent
        maxW={maxWidth}
        bg="panelBg"
        borderWidth="1px"
        borderColor="border"
        display="flex"
        flexDirection="column"
        maxH="calc(100vh - 56px)"
        overflow="hidden"
      >
        <ModalShellHeader
          step={step}
          icon={headerIcon}
          addon={headerAddon}
          onClose={onClose}
          dismissDisabled={dismissDisabled}
        />
        {subHeader && (
          <Box flex="none" px="22px" py="14px" borderBottomWidth="1px" borderColor="border.subtle">
            {subHeader}
          </Box>
        )}
        <DialogBody flex="1" minH="0" overflowY="auto" px="22px" pt="18px" pb="20px">
          {children}
        </DialogBody>
        {footer && (
          <DialogFooter
            flex="none"
            px="22px"
            pt="16px"
            pb="20px"
            borderTopWidth="1px"
            borderColor="border.subtle"
          >
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </DialogRoot>
  );
};

export default ModalShell;
